import React, { useState, useEffect, useContext } from "react";
import {
  getAuth,
  createUserWithEmailAndPassword,
  signInWithPopup,
  onAuthStateChanged,
  OAuthProvider,
  GoogleAuthProvider,
  linkWithCredential,
  EmailAuthProvider,
  linkWithPopup,
} from "firebase/auth";
import { useNavigate } from "react-router-dom";
import "./SignIn.css"; // Import a CSS file for styling
import { doc, setDoc, getDocs, query, collection, where } from "firebase/firestore";
import { nanoid } from "nanoid";
import { Snackbar, Alert, Button } from "@mui/material";
import Cookies from "js-cookie";
import { EnvContext } from "../../context/EnvContext";
import { UserContext } from "../../context/UserContext";
import config from "../../config";
//
import createReferral from "../../functions/createReferral";
import { db, logAnalyticsEvent } from "../../firebase/firebaseConfig";
import { useModal } from "../../context/ModalContext"; // Import the ModalContext hook
import sendConfirmationEmail from "../../functions/sendConfirmationEmail";
import axios from "axios";
import analyticsHelper from "../../utils/analyticsHelper";

const breakpoints = [755]
const mq = breakpoints.map(bp => `@media (min-width: ${bp}px)`)

const SignUp = () => {
  const environment = useContext(EnvContext);
  const navigate = useNavigate();
  const [email, setEmail] = useState("");
  const [password, setPassword] = useState("");
  const [confirmPassword, setConfirmPassword] = useState(""); // State for confirming password
  const [error, setError] = useState("");
  const [openSnackbar, setOpenSnackbar] = useState(false); // Alert visibility
  const { currentModal, setCurrentModal, modalCallback } = useModal(); // Use the ModalContext hook
  const [isSubmitting, setIsSubmitting] = useState(false); // Track if the form is submitting
  const [signUpSuccess, setSignUpSuccess] = useState(false); // Track sign-up success
  const [errorMessages, setErrorMessages] = useState([]);
  const [fieldErrors, setFieldErrors] = useState({
    email: false,
    password: false,
    confirmPassword: false,
  });

  const auth = getAuth();

  const addToKlaviyoEmailList = async (user) => {
    axios.post(`${config.apiConfig.API_ENDPOINT_URL}/klaviyo/subscribeToList`, { user, list: "Email" });
  };

  //
  const collectionPath = environment === "staging" ? config.environment.staging.collectionPath : config.environment.production.collectionPath;

  //
  const emailRegex = /^\S+@\S+\.\S+$/;

  const clearErrors = () => {
    setErrorMessages([]);
    setFieldErrors({ email: false, password: false, confirmPassword: false });
  };

  const validateForm = () => {
    const msgs = [];
    const fld = { email: false, password: false, confirmPassword: false };

    if (!emailRegex.test(email.trim())) {
      msgs.push("Please enter a valid email address");
      fld.email = true;
    }
    if (password.length < 6) {
      msgs.push("Password must be at least 6 characters");
      fld.password = true;
    }
    if (password !== confirmPassword) {
      msgs.push("Passwords do not match");
      fld.password = true;
      fld.confirmPassword = true;
    }

    setErrorMessages(msgs);
    setFieldErrors(fld);

    return msgs.length === 0;
  };

  useEffect(() => {
    if (signUpSuccess && window.location.pathname === "/checkout") {
      if (modalCallback) {
        modalCallback();
      }
      setCurrentModal(null);
    }
  }, [signUpSuccess, modalCallback]);

  // Analytics
  useEffect(() => {
    logAnalyticsEvent("sign_up_view", {
      page_title: environment === "staging" ? "STAGING - Sign Up Page" : "Sign Up Page",
      page_location: window.location.href,
      page_path: window.location.pathname,
      environment: environment,
    });
  }, [environment]);

  const createUserInFirestore = async (user, userConfirmationCode) => {
    const firestore = db;
    const userDocRef = doc(firestore, "Users/UsersList/DataBase", user.uid);

    // Add the user to Firestore
    await setDoc(
      userDocRef,
      {
        uid: user.uid,
        email: user.email,
        isAnonymous: false, // Make sure isAnonymous is set to false after linking or signing up
        createdAt: new Date(),
        user_code: userConfirmationCode, // Generate a random 6-character user code
      },
      { merge: true }
    );
  };

  const afterUserSignUp = async (user) => {
    await analyticsHelper.identify(user);
    const userConfirmationCode = nanoid(6);

    await createUserInFirestore(user, userConfirmationCode);
    await sendConfirmationEmail(user.email, "", user.uid, userConfirmationCode);

    try {
      addToKlaviyoEmailList(user);
    } catch (error) {
      // TODO: Add issue tracker and silently track failures like this
      console.error("Failed to add user to klaviyo list");
    }
  };

  const linkAnonymousAccount = async (provider) => {
    try {
      // If the user is signing up with email and password
      if (provider === "email") {
        const credential = EmailAuthProvider.credential(email, password);
        const linkedUser = await linkWithCredential(auth.currentUser, credential);
        return linkedUser.user;
      }
      // If the user is using a third-party provider like Google or Apple
      else {
        const linkedUser = await linkWithPopup(auth.currentUser, provider);
        return linkedUser.user;
      }
    } catch (error) {
      if (error.code === "auth/email-already-in-use") {
        setErrorMessages(["This email address is already taken"]);
        setFieldErrors({ email: true, password: false, confirmPassword: false });
      } else {
        setErrorMessages([error.message]);
      }
    }
  };

  const handleSignUp = async (event) => {
    event.preventDefault();
    clearErrors();
    if (!validateForm()) return;

    // Check if passwords match
    if (password !== confirmPassword) {
      setError("Passwords do not match.");
      return;
    }

    setIsSubmitting(true); // Disable buttons

    /* Referral program - for referral_link */
    let referrerUid = null;
    const referralData = await checkReferralCriteria();

    const nestedReferralData = referralData?.referralData;

    if (nestedReferralData) {
      referrerUid = nestedReferralData.referrerId || null; // Update referrerUid instead of redeclaring
    } else {
    }

    try {
      if (auth.currentUser && auth.currentUser.isAnonymous) {
        try {
          const linkedUser = await linkAnonymousAccount("email"); // Link Email account
          setError("");
          await afterUserSignUp(linkedUser); // Update Firestore and set isAnonymous to false

          // Access the nested referral data object

          if (referralData && referrerUid) {
            await createReferral({
              collectionPath,
              nestedReferralData,
              referrerUid,
              refereeUid: linkedUser.uid,
            });
          }

          /* end of referral program */

          // Send email verification
          // await sendEmailVerification(linkedUser);

          setOpenSnackbar(true);
          setSignUpSuccess(true); // Set sign-up success to true

          // navigate(`/`); // Remove navigation
          // setCurrentModal(null); // Close the modal - handled conditionally
        } catch (error) {
          setError(error.message);
        }
        logAnalyticsEvent("sign_up_anonymous", {
          method: "anonymous",
          environment: environment,
        });
      } else {
        createUserWithEmailAndPassword(auth, email, password)
          .then(async (userCredential) => {
            const user = userCredential.user;
            setError("");

            await afterUserSignUp(user);

            /* Referral program - for referral_link */

            if (referralData && referrerUid) {
              await createReferral({
                collectionPath,
                nestedReferralData,
                referrerUid,
                refereeUid: user.uid,
              });
            }

            /* end of referral program */

            // Send email verification
            // await sendEmailVerification(user);
            setSignUpSuccess(true); // Set sign-up success to true

            // navigate(`/`); // Remove navigation
            // setCurrentModal(null); // Close the modal - handled conditionally
          })
          .catch((error) => {
            if (error.code === "auth/email-already-in-use") {
              setErrorMessages(["This email address is already taken"]);
              setFieldErrors({ email: true, password: false, confirmPassword: false });
            } else {
              setErrorMessages([error.message]);
            }
          });
        logAnalyticsEvent("sign_up_email", {
          method: "email",
          environment: environment,
        });
      }
    } finally {
      setIsSubmitting(false); // Re-enable buttons
    }
  };

  const handleSuccessClick = () => {
    currentModal ? setCurrentModal(null) : navigate('/');
  }

  const handleGoogleSignIn = async () => {
    setIsSubmitting(true); // Disable buttons
    const provider = new GoogleAuthProvider();

    /* Referral program - for referral_link */
    let referrerUid = null;
    const referralData = await checkReferralCriteria();

    // Access the nested referral data object
    const nestedReferralData = referralData?.referralData;

    if (nestedReferralData) {
      referrerUid = nestedReferralData.referrerId || null; // Update referrerUid instead of redeclaring
    } else {
    }

    /* end of referral program */

    try {
      if (auth.currentUser && auth.currentUser.isAnonymous) {
        try {
          const linkedUser = await linkAnonymousAccount(provider); // Link Google account
          await afterUserSignUp(linkedUser, "");

          /* Referral program - for referral_link */
          if (nestedReferralData && referrerUid) {
            await createReferral({
              collectionPath,
              nestedReferralData,
              referrerUid,
              refereeUid: linkedUser.uid,
            });
          }
          setSignUpSuccess(true); // Set sign-up success to true
          // navigate(`/`); // Remove navigation
          // setCurrentModal(null); // Close the modal - handled conditionally
        } catch (error) {
          setError(error.message);
        }
      } else {
        signInWithPopup(auth, provider)
          .then(async (result) => {
            await afterUserSignUp(result.user, "");

            /* Referral program - for referral_link */
            if (nestedReferralData && referrerUid) {
              await createReferral({
                collectionPath,
                nestedReferralData,
                referrerUid,
                refereeUid: result.user.uid,
              });
            }
            setSignUpSuccess(true); // Set sign-up success to true
            // navigate(`/`); // Remove navigation
            // setCurrentModal(null); // Close the modal - handled conditionally
          })
          .catch((error) => {
            setError(error.message);
          });
      }
    } finally {
      setIsSubmitting(false); // Re-enable buttons
    }

    logAnalyticsEvent("sign_up_google", {
      method: "google",
      environment: environment,
    });
  };

  const handleAppleSignIn = async () => {
    setIsSubmitting(true); // Disable buttons
    const provider = new OAuthProvider("apple.com");

    /* Referral program - for referral_link */
    const referralData = await checkReferralCriteria();
    let referrerUid = null;

    // Access the nested referral data object
    const nestedReferralData = referralData?.referralData;

    if (nestedReferralData) {
      referrerUid = nestedReferralData.referrerId || null; // Update referrerUid instead of redeclaring
    } else {
    }

    try {
      if (auth.currentUser && auth.currentUser.isAnonymous) {
        try {
          const linkedUser = await linkAnonymousAccount(provider); // Link Apple account
          await afterUserSignUp(linkedUser, "");

          /* Referral program - for referral_link */
          if (referralData && referrerUid) {
            await createReferral({
              collectionPath,
              nestedReferralData,
              referrerUid,
              refereeUid: linkedUser.uid,
            });
          }
          /* end of referral program */
          setSignUpSuccess(true); // Set sign-up success to true
          // navigate(`/`); // Remove navigation
          // setCurrentModal(null); // Close the modal - handled conditionally
        } catch (error) {
          setError(error.message);
        }
      } else {
        signInWithPopup(auth, provider)
          .then(async (result) => {
            await afterUserSignUp(result.user, "");

            /* Referral program - for referral_link */
            if (referralData && referrerUid) {
              await createReferral({
                collectionPath,
                nestedReferralData,
                referrerUid,
                refereeUid: result.user.uid,
              });
            }
            /* end of referral program */
            setSignUpSuccess(true); // Set sign-up success to true
            // navigate(`/`); // Remove navigation
            // setCurrentModal(null); // Close the modal - handled conditionally
          })
          .catch((error) => {
            setError(error.message);
          });
      }
    } finally {
      setIsSubmitting(false); // Re-enable buttons
    }
    logAnalyticsEvent("sign_up_apple", {
      method: "apple",
      environment: environment,
    });
  };

  // Referral program - for referral_link
  const checkReferralCriteria = async () => {
    // Retrieve referral code from cookies
    const referralCode = Cookies.get("userInviteCode");
    if (!referralCode) {
      console.log("No referral code found in cookies");
      return null; // If no referral code, return null
    }

    // Ensure collectionPath is defined
    if (!collectionPath) {
      console.error("Collection path is undefined");
      return null;
    }

    try {
      const firestore = db;

      // Query to find user by user_code
      const referralUserQuery = query(collection(firestore, "Users/UsersList/DataBase"), where("user_code", "==", referralCode));
      const referralUserSnapshot = await getDocs(referralUserQuery);

      if (!referralUserSnapshot.empty) {
        const referralUserDoc = referralUserSnapshot.docs[0];
        const referralUserData = referralUserDoc.data();

        // Query for active referral based on referralCode
        const referralQuery = query(
          collection(firestore, `${collectionPath}Marketing/Referrals/DataBase`),
          where("name", "==", "referral_link"),
          where("active", "==", true)
        );

        const querySnapshot = await getDocs(referralQuery);

        if (!querySnapshot.empty) {
          const referralData = {
            inviteCode: referralCode,
            referrerId: referralUserDoc.id,
            referrerEmail: referralUserData.email,
            ...querySnapshot.docs[0].data(),
          };

          return { referralData };
        } else {
          console.log("No active referral found");
          return null;
        }
      } else {
        console.log("No user found with the provided referral code");
        return null;
      }
    } catch (error) {
      console.error("Error fetching referral data:", error);
      return null;
    }
  };

  return (
    <div css={{ padding: "0 10px", maxWidth: "1140px", margin: "0 auto" }}>
      <div css={{ height: "50px" }} />
      <div onClick={() => navigate(`/`)} css={{ cursor: "pointer", display: "flex", justifyContent: "center" }}>
        <img src="/images/fullproof_logo_black.svg" alt="Fullproof" css={{
          height: "40px", marginBottom: 10, [mq[0]]: { height: "50px" },
        }} />
      </div>
      <div css={{ height: "50px" }} />


      {signUpSuccess ? (
        // Show success message
        <div css={{ display: "flex", justifyContent: "center", flexDirection: "column", alignContent: "center", alignItems: "center" }}>
          <img src="/images/success.png" alt="Success" css={{ width: "240px", marginBottom: "20px" }} />
          <p css={{ ...styles.fontLarge, textAlign: "center", marginBottom: "20px" }}>Success!</p>

          <p css={{ textAlign: "center", marginBottom: "20px" }}>
            Please check your email for confirmation of account registration. Be sure to click the link in the email to verify your email address.
          </p>
          <div
            variant="contained"
            color="primary"
            fullWidth
            onClick={handleSuccessClick}
            css={{ background: "black", width: "100%", padding: "8px 0", borderRadius: 100, color: "white", textAlign: "center" }}
          >
            Shop Now
          </div>

          <div css={{ height: 20 }} />
        </div>
      ) : (
        // Show sign-up form
        <>
          {errorMessages.length > 0 && (
            <div css={styles.formErrorsWrapper}>
              {errorMessages.map((msg, idx) => (
                <p key={idx} css={styles.errorText}>
                  {msg}
                </p>
              ))}
            </div>
          )}
          <div css={{ ...styles.fontLarge, paddingBottom: 10, color: "black" }}>Sign Up</div>
          <form className="signin-form" onSubmit={handleSignUp} noValidate>
            <label css={styles.fontSmall}>Enter your email address</label>
            <input
              type="email"
              placeholder="Email address"
              value={email}
              onChange={(e) => setEmail(e.target.value)}
              required
              disabled={isSubmitting}
              autoComplete="off"
              css={{
                ...styles.inputBase,
                ...(fieldErrors.email ? styles.inputError : {}),
              }}
            />
            <label css={styles.fontSmall}>Enter a password (at least 6 characters)</label>
            <input
              type="password"
              placeholder="Password"
              value={password}
              onChange={(e) => setPassword(e.target.value)}
              required
              disabled={isSubmitting}
              autoComplete="new-password"
              css={{
                ...styles.inputBase,
                ...(fieldErrors.password ? styles.inputError : {}),
              }}
            />
            <label css={styles.fontSmall}>Re-enter your password</label>
            <input
              type="password"
              placeholder="Confirm Password"
              value={confirmPassword}
              onChange={(e) => setConfirmPassword(e.target.value)}
              required
              onPaste={(e) => e.preventDefault()} // Disable paste to avoid copy-pasting
              disabled={isSubmitting}
              autoComplete="new-password"
              css={{
                ...styles.inputBase,
                ...(fieldErrors.password ? styles.inputError : {}),
              }}
            />
            <button
              type="submit"
              className="btn btn-primary"
              disabled={isSubmitting}
              css={{ display: "flex", justifyContent: "center", alignItems: "center", borderRadius: 100 }}
            >
              Sign Up with Email
            </button>
          </form>

          <button
            className="btn btn-secondary"
            onClick={handleGoogleSignIn}
            disabled={isSubmitting}
            css={{
              display: "flex",
              justifyContent: "center",
              alignItems: "center",
              background: "white",
              borderRadius: 100,
              border: "1px solid black",
              color: "black",
            }}
          >
            <img
              alt="Google Logo"
              src="/images/google_logo.png"
              css={{
                height: "18px",
                top: "0px",
                marginRight: "8px",
                position: "relative",
              }}
            />{" "}
            Sign Up with Google
          </button>

          {/* Temporarly removed until Apple SignIn is fixed */}
          {/* <button
            className="btn btn-primary btn-apple"
            onClick={handleAppleSignIn}
            disabled={isSubmitting}
            css={{
              display: "flex",
              justifyContent: "center",
              alignItems: "center",
              background: "white",
              borderRadius: 100,
              border: "1px solid black",
              color: "black",
            }}
          >
            <img alt="Apple logo" src="/images/apple_logo.png" css={{ height: "18px", top: "0px", marginRight: "8px", position: "relative" }} /> Sign Up with Apple
          </button> */}

          <p className="footer-text">
            Already have an account?{" "}
            <span onClick={() => setCurrentModal("signIn")} css={{ cursor: "pointer", color: "blue" }}>
              Log In
            </span>
          </p>

          <p className="footer-text">
            By creating an account you agree to our{" "}
            <a href="/legal/terms-of-service" css={{ textDecoration: "none" }}>
              Terms of Service
            </a>
          </p>
          <p
            className="footer-text"
            onClick={() =>
              // close modal and navigate to homepage
              setCurrentModal(null) & navigate(`/`)
            }
            css={{ cursor: "pointer" }}
          >
            Back to <u>Homepage</u>.
          </p>

          <div css={{ height: 20 }} />
        </>
      )}
    </div>
  );
};

export default SignUp;

const styles = {
  fontSmall: {
    fontSize: ".85rem",
    paddingBottom: 5,
    color: "black",
    [mq[0]]: {
      fontSize: "1.2rem",
    },
  },
  fontLarge: {
    fontSize: "1.2rem",
    fontWeight: 600,
    [mq[0]]: {
      fontSize: "1.5rem",
    },
  },
  inputBase: {
    width: "100%",
    padding: "8px 12px",
    marginBottom: 16,
    borderRadius: 4,
    border: "1px solid #ccc",
    fontSize: "1rem",
    boxSizing: "border-box",
  },
  inputError: { border: "1px solid #d32f2f" },
  errorText: {
    margin: "0 0 6px",
    color: "#d32f2f",
    fontSize: "0.85rem",
    fontWeight: 500,
    [mq[0]]: {
      fontSize: "1.2rem",
    },
  },
  formErrorsWrapper: { marginBottom: 12 },
};
